import React from 'react';
import { useHandleSignInCallback, useLogto } from '@logto/react';
import { loginUser } from '../../store/user/userSlice';
import { useAppDispatch } from '../../store/hooks';
import { useNavigate } from 'react-router';
import FullscreenLoading from '../../components/FullscreenLoading';
import Logger from '../../utils/logger';
import { Organisation } from '../../types/user';
import { getUserFeatureFlags, getUserOrgs, getUserSystemRoles, getUserSystemState } from '../../utils/user';
import { initialSetup } from '../../store/system/systemSlice';
import { setUserRoles } from '../../store/tab/tabSlice';

const RESERVED_ROLES = ['Elastic Admin Users', 'Prometheus Administrators'];

const CallbackPage: React.FC = () => {
	const { fetchUserInfo, getAccessToken, getOrganizationTokenClaims } = useLogto();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { isLoading, error } = useHandleSignInCallback(() => {
		fetchUserInfo().then(async (info) => {
			const postSignInUrl = localStorage.getItem('redirPage');
			const accessToken = await getAccessToken(import.meta.env.VITE_LOGTO_IDENTIFIER);

			if (accessToken) {
				const orgs: Organisation[] = await getUserOrgs(getOrganizationTokenClaims, info);

				dispatch(initialSetup(await getUserSystemState(accessToken)));
				dispatch(setUserRoles({ roles: getUserSystemRoles(orgs, info?.roles), flags: await getUserFeatureFlags(accessToken) }));

				const userRoles = info?.roles ? info.roles.filter((role) => !RESERVED_ROLES.includes(role)) : [];

				dispatch(
					loginUser({
						id: info?.sub || '0', // must always have an ID after logging in
						name: info?.name,
						email: info?.email,
						orgs: orgs,
						roles: [...userRoles, 'DEFAULT-OPERATION'],
					})
				);
				navigate(postSignInUrl || '/');
			} else {
				Logger.error('Unable to fetch one or more of API access token/ claims/ refresh token from LogTo');
			}
		});
	});

	return <FullscreenLoading failed={!isLoading && error && error.name !== ''} />;
};

export default CallbackPage;
